import { toNumber } from "../lib/format";
import type { HistoryRow } from "./HistoryTable";

export type GroupKey = "costCenter" | "supplier" | "document" | "month" | "price";
export const GROUP_LABELS: Record<GroupKey, string> = {
  costCenter: "Kostenstelle", supplier: "Großhändler", document: "Beleg", month: "Monat", price: "Preis",
};
export type HistoryGroup<T> = { key: string; label: string; rows: HistoryRow<T>[]; sourceRows: HistoryRow<T>[] };
export type HistoryExportGroup<T> = { label: string; rows: readonly T[]; summary: GroupSummary };
export type GroupSummary = {
  count: number; qty: number | null; uom: string | null; total: number | null;
  comparedCount: number; average: number | null; best: number | null;
  priceUom: string | null; difference: number | null; percent: number | null;
};
const MONTH = new Intl.DateTimeFormat("de-AT", { month: "long", year: "numeric" });
const PRICE = new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR", maximumFractionDigits: 8 });

export function groupRows<T>(rows: HistoryRow<T>[], key: GroupKey | null): HistoryGroup<T>[] {
  if (!key) return [{ key: "", label: "", rows, sourceRows: rows }];
  const groups = new Map<string, HistoryGroup<T>>();
  for (const row of rows) {
    let label: string;
    let id: string;
    if (key === "price") {
      const price = toNumber(row.unitPrice ?? null);
      label = price === null ? "Ohne Preis" : `${PRICE.format(price)} / ${row.uom?.trim() || "Einheit unbekannt"}`;
      id = JSON.stringify([price, row.uom?.trim() || null]);
    } else if (key === "month") {
      const date = row.date ? new Date(`${row.date.slice(0, 10)}T00:00:00`) : null;
      label = date && Number.isFinite(date.valueOf()) ? MONTH.format(date) : "Ohne Datum";
      id = label;
    } else {
      label = row.groupLabels?.[key]?.trim() || (key === "costCenter" ? "Nicht zugeordnet" : "Ohne Angabe");
      id = row.groupIds?.[key] ?? label;
    }
    const bucket = groups.get(id);
    if (bucket) bucket.rows.push(row);
    else { const items = [row]; groups.set(id, { key: id, label, rows: items, sourceRows: items }); }
  }
  return [...groups.values()];
}

/** All totals use original evidence, including behind merged display rows.
 * Price metrics only compare positive invoice quantities for one article/unit.
 * Missing values are never silently converted to zero or advertised as full sums.
 */
export function groupSummary<T>(displayRows: readonly HistoryRow<T>[]): GroupSummary {
  const rows = displayRows.flatMap(row => row.originalRows ?? [row]);
  const qtys = rows.map(row => toNumber(row.qty ?? null));
  const totals = rows.map(row => toNumber(row.total ?? null));
  const units = new Set(rows.map(row => row.uom?.trim() || null));
  const comparable = rows.filter(row => row.priceComparable && row.comparisonKey && row.uom?.trim()
    && (toNumber(row.qty ?? null) ?? 0) > 0 && (toNumber(row.unitPrice ?? null) ?? 0) > 0);
  const identities = new Set(comparable.map(row => JSON.stringify([row.comparisonKey, row.uom?.trim()])));
  const eligible = identities.size === 1 ? comparable : [];
  let quantity = 0, weighted = 0, referenceTotal = 0, difference = 0;
  let best: number | null = null;
  let referenceKnown = eligible.length > 0;
  for (const row of eligible) {
    const qty = toNumber(row.qty!)!, price = toNumber(row.unitPrice!)!;
    quantity += qty; weighted += qty * price; best = best === null ? price : Math.min(best, price);
    const delta = toNumber(row.deviation?.amount ?? null);
    const reference = delta === null ? null : price - delta;
    if (reference === null || reference <= 0 || row.referenceUom?.trim() !== row.uom?.trim()) referenceKnown = false;
    else { referenceTotal += reference * qty; difference += delta! * qty; }
  }
  return {
    count: rows.length,
    qty: units.size === 1 && !units.has(null) && qtys.every(q => q !== null) ? qtys.reduce<number>((sum, q) => sum + q!, 0) : null,
    uom: units.size === 1 ? [...units][0] : null,
    total: totals.every(t => t !== null) ? totals.reduce<number>((sum, t) => sum + t!, 0) : null,
    comparedCount: eligible.length, average: quantity > 0 ? weighted / quantity : null, best,
    priceUom: eligible[0]?.uom ?? null,
    difference: referenceKnown ? difference : null,
    percent: referenceKnown && referenceTotal > 0 ? difference / referenceTotal * 100 : null,
  };
}
